import { SIDEBAR_WIDTH } from './appNavigation';

/** Horizontal padding for main content and header rows that sit above it. */
export const APP_CONTENT_GUTTER = { xs: 2, md: 3 } as const;

/**
 * Optional readable width for long single-column form stacks.
 * Prefer applying to form stacks, not the app shell — the main Container
 * fills the flex column beside the sidebar with no pixel maxWidth so reduced
 * Chrome zoom (larger CSS viewport) does not leave empty horizontal bands.
 */
export const FORM_STACK_MAX_WIDTH = 1100;

/**
 * Scroll padding below the md breakpoint when the bottom nav and AI Advisor
 * button are both fixed. Clears the nav, the gap under the button, the button,
 * and the same gap above it, plus the device safe-area inset.
 */
export function mobileWorkspaceScrollPadding(
  navHeightPx: number,
  fabSizePx: number,
  fabGapPx: number,
): string {
  return `calc(${navHeightPx}px + ${fabGapPx}px + ${fabSizePx}px + ${fabGapPx}px + env(safe-area-inset-bottom, 0px))`;
}

/** Desktop AI Advisor button inset. Matches the Fab `bottom` at the md breakpoint. */
export const DESKTOP_ADVISOR_FAB_BOTTOM_PX = 24;

/**
 * Scroll padding at md and above, where the bottom nav is gone and the advisor
 * button sits `DESKTOP_ADVISOR_FAB_BOTTOM_PX` above the viewport edge.
 */
export function desktopAdvisorScrollPadding(
  fabSizePx: number,
  fabBottomPx: number,
  fabGapPx: number,
): string {
  return `calc(${fabBottomPx}px + ${fabSizePx}px + ${fabGapPx}px)`;
}

/** MUI `md`. Below this the fixed bottom nav is showing. */
export const WORKSPACE_SIDEBAR_MIN_PX = 900;

export function workspaceScrollPaddingForWidth(
  viewportWidthPx: number,
  navHeightPx: number,
  fabSizePx: number,
  fabGapPx: number,
  fabBottomDesktopPx: number = DESKTOP_ADVISOR_FAB_BOTTOM_PX,
): string {
  if (viewportWidthPx < WORKSPACE_SIDEBAR_MIN_PX) {
    return mobileWorkspaceScrollPadding(navHeightPx, fabSizePx, fabGapPx);
  }
  return desktopAdvisorScrollPadding(fabSizePx, fabBottomDesktopPx, fabGapPx);
}

/**
 * MUI `lg`. At this width the hero grid's own `lg` columns place the score
 * beside the footprint. Below it, a tall stacked hero pushes the metric row
 * into the bottom of a 900px viewport.
 */
export const DASHBOARD_HERO_DESKTOP_MIN_PX = 1200;

/**
 * Pair the score with the footprint from 600px until the desktop hero columns
 * take over. That keeps the metric row above the bottom nav below 900px and
 * inside the initial viewport at 900px and 901px.
 */
export function dashboardHeroLiftsMetricRow(viewportWidthPx: number): boolean {
  return viewportWidthPx >= 600 && viewportWidthPx < DASHBOARD_HERO_DESKTOP_MIN_PX;
}

/**
 * Right inset on the dashboard metric row at md+, so the advisor button
 * (56px, 24px from the right) does not cover the workforce tile.
 */
export const DASHBOARD_ADVISOR_CARD_GUTTER_PX = 80;

export function dashboardAdvisorCardGutterPx(viewportWidthPx: number): number {
  return viewportWidthPx >= WORKSPACE_SIDEBAR_MIN_PX ? DASHBOARD_ADVISOR_CARD_GUTTER_PX : 0;
}

/**
 * Top margin of the dashboard metric row, in theme spacing units (8px).
 * From 600px until the sidebar, that margin is removed. The measured row sat
 * 8px under the Advisor at 600×900; dropping the 20px margin leaves about 12px.
 * Below 600px and from 900px up, the row keeps its 2.5 spacing.
 */
export const DASHBOARD_METRIC_ROW_MARGIN_TOP = { xs: 2.5, sm: 0, md: 2.5 } as const;

export function dashboardMetricRowMarginTop(viewportWidthPx: number): number {
  if (viewportWidthPx >= 600 && viewportWidthPx < WORKSPACE_SIDEBAR_MIN_PX) {
    return DASHBOARD_METRIC_ROW_MARGIN_TOP.sm;
  }
  return DASHBOARD_METRIC_ROW_MARGIN_TOP.xs;
}

/**
 * Horizontal scroll host for wide tables / dense numeric grids.
 * Prefer wrapping bare `<Table>` / native tables so the page itself does not overflow.
 */
export const scrollableTableHostSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
} as const;

/** Scrollable MUI Tabs for dense label sets on narrow viewports. */
export const scrollableTabsSx = {
  minWidth: 0,
  maxWidth: '100%',
  '& .MuiTabs-scroller': { minWidth: 0 },
} as const;

/**
 * Nested cards / flex children in the main column (sidebar shrinks content width).
 * Apply on Card, CardContent, Paper KPI hosts, and flex:1 text columns.
 */
export const contentBoxSx = {
  minWidth: 0,
  maxWidth: '100%',
} as const;

/**
 * Four-up metric tiles: 1 col → 2 col → 4 col.
 * Defer 4-across to `lg` so `md` viewports with the desktop sidebar stay readable
 * (~640–900px content width while viewport still reports `md`).
 */
export const KPI_TILE_GRID = { xs: 12, sm: 6, lg: 3 } as const;

/**
 * Three-up metric / chart tiles: 1 → 2 → 3 columns.
 * Defer three-across to `lg` (sidebar content band).
 */
export const TRI_TILE_GRID = { xs: 12, sm: 6, lg: 4 } as const;

/** Hide native scrollbar chrome on the shell scrollports. Overflow scrolling stays. */
export const hiddenShellScrollbarSx = {
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
    width: 0,
    height: 0,
  },
} as const;

/**
 * Left inset for full-width header rows (breadcrumbs, page banner) so they line up
 * with the main column when the desktop sidebar is visible.
 */
export function headerMainColumnInsetSx(showSidebar: boolean, desktopSidebarCollapsed = false) {
  if (!showSidebar || desktopSidebarCollapsed) {
    return { px: APP_CONTENT_GUTTER };
  }

  return {
    pl: {
      xs: APP_CONTENT_GUTTER.xs,
      md: `calc(${SIDEBAR_WIDTH}px + 24px)`,
    },
    pr: APP_CONTENT_GUTTER,
  };
}

export { SIDEBAR_WIDTH };
